import { useState, useCallback, useEffect } from 'react';
import { useStreamChat } from '../contexts/StreamChatContext';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

export const useGroupChats = () => {
  const { chatClient, user: currentUser } = useStreamChat();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchConversations = useCallback(async () => {
    if (!chatClient || !currentUser) { setLoading(false); return; }

    try {
      setLoading(true);
      const filter = { type: 'gaming', members: { $in: [String(currentUser.id)] } };
      const sort = [{ last_message_at: -1 }];
      const channels = await chatClient.queryChannels(filter, sort, { watch: true, state: true, limit: 50 });

      const formatted = channels.map(channel => {
        const data = channel.data || {};
        const lastMessage = channel.state.messages?.[channel.state.messages.length - 1];
        return {
          id: channel.id,
          cid: channel.cid,
          channel,
          name: data.name || channel.id,
          image: resolveImageUrl(data.image) || null,
          lastMessage: lastMessage ? {
            text: lastMessage.text,
            createdAt: lastMessage.created_at,
            userId: lastMessage.user?.id,
          } : null,
          unreadCount: channel.countUnread(),
          updatedAt: data.last_message_at || data.created_at,
        };
      });

      setConversations(formatted);
      setUnreadCount(formatted.reduce((sum, c) => sum + c.unreadCount, 0));
    } catch (err) {
      console.error('[useGroupChats] Error fetching:', err);
    } finally {
      setLoading(false);
    }
  }, [chatClient, currentUser]);

  const markAsRead = useCallback(async (channelId) => {
    if (!chatClient) return;
    try {
      const channel = chatClient.channel('gaming', channelId);
      await channel.markRead();
      setConversations(prev => prev.map(c => c.id === channelId ? { ...c, unreadCount: 0 } : c));
      setUnreadCount(prev => {
        const conv = conversations.find(c => c.id === channelId);
        return Math.max(0, prev - (conv?.unreadCount || 0));
      });
    } catch (err) {
      console.error('[useGroupChats] Error marking as read:', err);
    }
  }, [chatClient, conversations]);

  useEffect(() => {
    if (!chatClient) return;
    const handleNewMessage = (event) => {
      if (event.channel_type !== 'gaming') return;
      const userId = String(currentUser?.id);
      if (event.message?.user?.id === userId) {
        // Own message: just update last message preview
        setConversations(prev => prev.map(c =>
          c.id === event.channel_id
            ? { ...c, lastMessage: { text: event.message.text, createdAt: event.message.created_at, userId } }
            : c
        ));
        return;
      }
      setConversations(prev => prev.map(c =>
        c.id === event.channel_id
          ? { ...c, unreadCount: c.unreadCount + 1, lastMessage: { text: event.message.text, createdAt: event.message.created_at, userId: event.message.user?.id } }
          : c
      ));
      setUnreadCount(prev => prev + 1);
    };

    chatClient.on('message.new', handleNewMessage);
    return () => chatClient.off('message.new', handleNewMessage);
  }, [chatClient, currentUser]);

  useEffect(() => {
    if (chatClient && currentUser) fetchConversations();
  }, [chatClient, currentUser, fetchConversations]);

  return { conversations, loading, unreadCount, markAsRead, fetchConversations };
};

export default useGroupChats;
